import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ProfileSettings.css';

const ProfileSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    phone: '',
    preferences: {
      emailNotifications: true,
      smsNotifications: false
    }
  });

  // Fetch user profile data
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          navigate('/login');
          return;
        }
        
        const response = await axios.get('/api/user/profile', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success && response.data.user) {
          const user = response.data.user;
          setUserData({
            name: user.name || '',
            email: user.email || '',
            phone: user.phone || '',
            preferences: {
              emailNotifications: user.preferences?.emailNotifications ?? true,
              smsNotifications: user.preferences?.smsNotifications ?? false
            }
          });
        }
      } catch (error) {
        setMessage({
          text: error.response?.data?.message || 'Failed to load profile',
          type: 'error'
        });
        console.error('Error loading profile:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [navigate]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle notification preference changes
  const handlePreferenceChange = (e) => {
    const { name, checked } = e.target;
    setUserData(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        [name]: checked
      }
    }));
  };

  // Submit form to update profile
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        navigate('/login');
        return;
      }
      
      // Validate phone if provided
      if (userData.phone && !/^\+?[0-9]{10,15}$/.test(userData.phone)) {
        setMessage({
          text: 'Please enter a valid phone number in international format (e.g., +1XXXXXXXXXX)',
          type: 'error'
        });
        setLoading(false);
        return;
      }
      
      const response = await axios.put('/api/user/profile', {
        phone: userData.phone,
        emailNotifications: userData.preferences.emailNotifications,
        smsNotifications: userData.preferences.smsNotifications
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        setMessage({
          text: 'Profile updated successfully',
          type: 'success'
        });
      }
    } catch (error) {
      setMessage({
        text: error.response?.data?.message || 'Failed to update profile',
        type: 'error'
      });
      console.error('Error updating profile:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-settings">
      <div className="profile-settings-container">
        <h1>Profile Settings</h1>
        
        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={userData.name}
              readOnly
              disabled
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={userData.email}
              readOnly
              disabled
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="phone">Phone Number (for SMS notifications)</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={userData.phone}
              onChange={handleInputChange}
              placeholder="+1XXXXXXXXXX"
            />
            <small>Please use international format with country code (e.g., +1XXXXXXXXXX)</small>
          </div>
          
          <div className="form-group notification-preferences">
            <h2>Notification Preferences</h2>
            
            <div className="checkbox-group">
              <input
                type="checkbox"
                id="emailNotifications"
                name="emailNotifications"
                checked={userData.preferences.emailNotifications}
                onChange={handlePreferenceChange}
              />
              <label htmlFor="emailNotifications">
                Receive order status updates via email
              </label>
            </div>
            
            <div className="checkbox-group">
              <input
                type="checkbox"
                id="smsNotifications"
                name="smsNotifications"
                checked={userData.preferences.smsNotifications}
                onChange={handlePreferenceChange}
                disabled={!userData.phone}
              />
              <label htmlFor="smsNotifications">
                Receive order status updates via SMS
                {!userData.phone && <span className="notice"> (Requires phone number)</span>}
              </label>
            </div>
          </div>
          
          <div className="form-actions">
            <button 
              type="submit" 
              className="update-profile-button"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileSettings; 